import type { Screen } from '../../../shared/types';
import type {
  ValidationAdditionalCategorySection,
  ValidationConnectorSection,
  ValidationIssueActionParams,
  ValidationIssueFamilyPrimaryActionType,
  ValidationIssueFamilyTargetPage,
  ValidationMappingFocus,
  ValidationMappingReference,
  ValidationProcessSection,
  ValidationQuickFillFocus,
  ValidationQuickFillSuggestedCellValue,
} from '../../../shared/types/validationNavigation.types';
import { perf } from '../../../shared/lib/perfLogger';
import {
  resolveAdditionalCategoriesDestinationFocus,
  resolveConnectorDestinationFocus,
  resolveProcessDestinationFocus,
} from './validationDestinationFocus';
import { buildQuickFillSuggestedCellSignature } from './validationQuickFillRepairMetadata';

export interface ValidationIssueFamilyAction {
  label: string;
  route?: Screen;
  params?: ValidationIssueActionParams;
  type?: ValidationIssueFamilyPrimaryActionType;
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function normalizeFocusToken(value: string): string {
  return value.trim().toUpperCase().replace(/[\s_]+/g, '-');
}

function mapQuickFillLabels(items: string[]): string[] {
  const mappings: Record<string, string> = {
    'OBJECT-TYPE': 'OBJECT-TYPE',
    '#PCF-OBJECT-TYPE': 'OBJECT-TYPE',
    'SKEY': 'SKEY',
    '#PCF-SKEY': 'SKEY',
    'PIPING-SPEC': 'PIPING-SPEC',
    'PIX-PPG-SPEC': 'PIPING-SPEC',
    'PIP-PPG-SPEC': 'PIPING-SPEC',
    'MATERIAL': 'MATERIAL',
    'PIX-03-MATERIAL-ENG': 'MATERIAL',
    'DESIGN-STANDARD': 'DESIGN-STD',
    'PIX-02-STANDARD-ENG': 'DESIGN-STD',
    'PRESSURE-CLASS': 'PRESSURE-CLASS',
    'PIX-PPG-END1-PRESSURERATING': 'PRESSURE-CLASS',
    'INSULATION-SPEC': 'INSULATION-SPEC',
    'PIX-GN-2-INSULATIONSPEC': 'INSULATION-SPEC',
    'INSULATION-THICKNESS': 'INSULATION-THICKNESS',
    'INSULATIONTHICKNESS': 'INSULATION-THICKNESS',
    'PIX-GN-2-INSULATIONTHICKNESS': 'INSULATION-THICKNESS',
    'TRACING-SPEC': 'TRACING-SPEC',
    'PIX-GN-2-HEATTRACINGSPEC': 'TRACING-SPEC',
    'ITEM-DESCRIPTION': 'ITEM-DESCRIPTION',
    'PIX-01-DESCRIPTION-ENG': 'ITEM-DESCRIPTION',
    'END-TYPE-1': 'End1',
    'END-TYPE-2': 'End2',
    'END-TYPE-3': 'End3',
    'END-TYPE-4': 'End4',
    'PIX-PPG-END1-TYPE': 'End1',
    'PIX-PPG-END2-TYPE': 'End2',
    'PIX-PPG-END3-TYPE': 'End3',
    'PIX-PPG-END4-TYPE': 'End4',
    'ENDPOINT1': 'End1',
    'ENDPOINT2': 'End2',
    'ENDPOINT3': 'End3',
    'ENDPOINT4': 'End4',
    'END1': 'End1',
    'END2': 'End2',
    'END3': 'End3',
    'END4': 'End4',
  };

  return unique(
    items
      .map(normalizeFocusToken)
      .map((item) => mappings[item] || null)
      .filter((value): value is string => Boolean(value))
  );
}

const QUICK_FILL_AUTO_FILL_LABEL_PRIORITY = ['OBJECT-TYPE', 'SKEY', 'End1', 'End2', 'End3', 'End4'];

const GENERAL_MAPPING_FOCUS_TOKENS = new Set([
  'OBJECT-TYPE',
  '#PCF-OBJECT-TYPE',
  'SKEY',
  '#PCF-SKEY',
  'PIPING-SPEC',
  'PIP-PPG-SPEC',
  'PIX-PPG-SPEC',
  'SYSTEM-GROUPING',
]);

const OBJECT_TYPE_MAPPING_FOCUS_TOKENS = new Set([
  'END-TYPE-1',
  'END-TYPE-2',
  'END-TYPE-3',
  'END-TYPE-4',
  'END-TYPE1',
  'END-TYPE2',
  'END-TYPE3',
  'END-TYPE4',
  'END-POINT-1',
  'END-POINT-2',
  'END-POINT-3',
  'END-POINT-4',
  'END-POINT1',
  'END-POINT2',
  'END-POINT3',
  'END-POINT4',
  'ENDPOINT1',
  'ENDPOINT2',
  'ENDPOINT3',
  'ENDPOINT4',
  'END1',
  'END2',
  'END3',
  'END4',
  'MATERIAL',
  'PRESSURE-CLASS',
  'DESIGN-STANDARD',
  'DESIGN-STD',
  'INSULATION-SPEC',
  'INSULATION-THICKNESS',
  'INSULATIONTHICKNESS',
  'TRACING-SPEC',
  'ITEM-DESCRIPTION',
  'BOLT-LENGTH',
  'BOLT-DIAMETER',
  'BOLT-DIA',
]);

const PROJECT_PROPERTIES_MAPPING_FOCUS_TOKENS = new Set([
  'PROJECT-PROPERTIES',
  'PROJECT-IDENTIFIER',
  'PROJECT-NAME',
  'AREA',
]);

const PROCESS_CONDITIONS_MAPPING_FOCUS_TOKENS = new Set([
  'PROCESS-CONDITIONS',
  'ATTRIBUTE12',
  'ATTRIBUTE13',
  'ATTRIBUTE14',
  'ATTRIBUTE15',
  'ATTRIBUTE16',
  'ATTRIBUTE17',
  'ATTRIBUTE18',
  'ATTRIBUTE19',
  'ATTRIBUTE20',
  'ATTRIBUTE21',
  'ATTRIBUTE22',
  'ATTRIBUTE23',
]);

function resolveMappingReferenceForToken(
  normalizedToken: string
): ValidationMappingReference | null {
  if (GENERAL_MAPPING_FOCUS_TOKENS.has(normalizedToken)) {
    return {
      nodeType: 'general',
      label: 'General',
      attributes: [],
    };
  }

  if (PROJECT_PROPERTIES_MAPPING_FOCUS_TOKENS.has(normalizedToken)) {
    return {
      nodeType: 'projectProperties',
      label: 'Project properties',
      attributes: [],
    };
  }

  if (PROCESS_CONDITIONS_MAPPING_FOCUS_TOKENS.has(normalizedToken)) {
    return {
      nodeType: 'processConditions',
      label: 'Process conditions',
      attributes: [],
    };
  }

  if (/^ATTRIBUTE([1-9]|10)$/.test(normalizedToken)) {
    return {
      nodeType: 'projectProperties',
      label: 'Project properties',
      attributes: [],
    };
  }

  if (OBJECT_TYPE_MAPPING_FOCUS_TOKENS.has(normalizedToken)) {
    return {
      nodeType: 'objectType',
      nodeName: 'ALL',
      label: 'All mapping',
      attributes: [],
    };
  }

  return null;
}

function buildMappingReferences(
  items: string[]
): ValidationMappingReference[] {
  const references = new Map<string, ValidationMappingReference>();

  for (const item of items) {
    const trimmedItem = item.trim();
    if (!trimmedItem) {
      continue;
    }

    const normalizedItem = normalizeFocusToken(trimmedItem);
    const reference = resolveMappingReferenceForToken(normalizedItem);
    if (!reference) {
      continue;
    }

    const key = `${reference.nodeType}:${reference.nodeName ?? ''}`;
    const existing = references.get(key);
    if (existing) {
      if (!existing.attributes.includes(trimmedItem)) {
        existing.attributes.push(trimmedItem);
      }
      continue;
    }

    references.set(key, {
      ...reference,
      attributes: [trimmedItem],
    });
  }

  return Array.from(references.values()).sort((left, right) => {
    const priority = (value: ValidationMappingReference): number => {
      if (value.nodeType === 'general') {
        return 0;
      }

      if (value.nodeType === 'processConditions') {
        return 1;
      }

      if (value.nodeType === 'projectProperties') {
        return 2;
      }

      return 3;
    };

    return priority(left) - priority(right) || left.label.localeCompare(right.label);
  });
}

function buildMappingFocus(
  references: ValidationMappingReference[]
): ValidationMappingFocus | undefined {
  const primaryReference = references[0];
  if (!primaryReference) {
    return undefined;
  }

  return {
    nodeType: primaryReference.nodeType,
    ...(primaryReference.nodeName ? { nodeName: primaryReference.nodeName } : {}),
  };
}

function buildQuickFillFocus(
  labels: string[],
  suggestedCellValues?: ValidationQuickFillSuggestedCellValue[]
): ValidationQuickFillFocus | undefined {
  const normalizedLabels = unique(labels.filter(Boolean));
  if (normalizedLabels.length === 0) {
    return undefined;
  }

  const autofillLabels = QUICK_FILL_AUTO_FILL_LABEL_PRIORITY.filter((label) =>
    normalizedLabels.includes(label)
  );

  return {
    labels: normalizedLabels,
    primaryLabel: autofillLabels[0] ?? normalizedLabels[0] ?? null,
    rowCandidates: [],
    elementIds: [],
    familyTypeKeys: [],
    preferredRowMode: 'instance',
    autofillLabels,
    autofillMode: 'overwrite',
    ...(suggestedCellValues && suggestedCellValues.length > 0 ? { suggestedCellValues } : {}),
  };
}

function mergeQuickFillFocus(
  left: ValidationQuickFillFocus | undefined,
  right: ValidationQuickFillFocus | undefined
): ValidationQuickFillFocus | undefined {
  if (!left) {
    return right;
  }

  if (!right) {
    return left;
  }

  const suggestedCellValueBySignature = new Map<string, ValidationQuickFillSuggestedCellValue>();
  [...(left.suggestedCellValues ?? []), ...(right.suggestedCellValues ?? [])].forEach((cell) => {
    const signature = buildQuickFillSuggestedCellSignature([cell]);
    if (signature && !suggestedCellValueBySignature.has(signature)) {
      suggestedCellValueBySignature.set(signature, cell);
    }
  });
  const suggestedCellValues = Array.from(suggestedCellValueBySignature.values());
  const labels = unique([
    ...left.labels,
    ...right.labels,
    ...suggestedCellValues.map((cell) => cell.label),
  ].filter(Boolean));
  const prioritizedAutofillLabels = QUICK_FILL_AUTO_FILL_LABEL_PRIORITY.filter((label) =>
    labels.includes(label)
  );
  const autofillLabels = unique([
    ...prioritizedAutofillLabels,
    ...left.autofillLabels,
    ...right.autofillLabels,
  ].filter((label) => labels.includes(label)));

  return {
    ...left,
    requestId: left.requestId ?? right.requestId,
    labels,
    primaryLabel: autofillLabels[0] ?? labels[0] ?? left.primaryLabel ?? right.primaryLabel ?? null,
    rowCandidates: unique([...left.rowCandidates, ...right.rowCandidates]),
    elementIds: unique([...left.elementIds, ...right.elementIds]),
    familyTypeKeys: unique([...left.familyTypeKeys, ...right.familyTypeKeys]),
    preferredRowMode:
      left.preferredRowMode === 'instance' || right.preferredRowMode === 'instance'
        ? 'instance'
        : left.preferredRowMode,
    autofillLabels,
    autofillMode:
      left.autofillMode === 'overwrite' || right.autofillMode === 'overwrite'
        ? 'overwrite'
        : 'fillEmpty',
    ...(suggestedCellValues.length > 0 ? { suggestedCellValues } : {}),
  };
}

function mergeActionQuickFillFocus(
  target: ValidationIssueFamilyAction,
  source: ValidationIssueFamilyAction
): ValidationIssueFamilyAction {
  const quickFillFocus = mergeQuickFillFocus(
    target.params?.quickFillFocus,
    source.params?.quickFillFocus
  );
  if (!quickFillFocus) {
    return target;
  }

  return {
    ...target,
    params: {
      ...target.params,
      quickFillFocus,
    },
  };
}

function toEnabledSectionList<T extends object>(state: T): Array<Extract<keyof T, string>> {
  return Object.entries(state as Record<string, boolean>)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key as Extract<keyof T, string>);
}

export function buildActionParams(
  action: ValidationIssueFamilyAction,
  targetPage: ValidationIssueFamilyTargetPage,
  affectedItems: string[]
): ValidationIssueActionParams | undefined {
  if (action.type !== 'navigate') {
    return undefined;
  }

  const normalizedItems = unique(affectedItems.map(normalizeFocusToken).filter(Boolean));
  if (targetPage === 'mapping') {
    const mappingReferences = buildMappingReferences(affectedItems);
    const mappingFocus = buildMappingFocus(mappingReferences);
    return {
      highlightedAttributes: normalizedItems,
      ...(mappingFocus ? { mappingFocus } : {}),
      ...(mappingReferences.length > 0 ? { mappingReferences } : {}),
    };
  }

  if (targetPage === 'quick-fill') {
    const existingQuickFillFocus = action.params?.quickFillFocus;
    const mappedQuickFillLabels = mapQuickFillLabels(normalizedItems);
    const focusLabels = unique([
      ...mappedQuickFillLabels,
      ...(existingQuickFillFocus?.labels ?? []),
    ]);
    const quickFillFocus = existingQuickFillFocus
      ? buildQuickFillFocus(focusLabels, existingQuickFillFocus.suggestedCellValues)
      : buildQuickFillFocus(mappedQuickFillLabels);
    if (!quickFillFocus) {
      perf.warn('VAL-Handoff', 'Quick Fill action has no focus payload', {
        actionLabel: action.label,
        actionRoute: action.route || null,
        targetPage,
        affectedItems,
        normalizedItems,
        mappedQuickFillLabels,
      });
    } else {
      perf.log('VAL-Handoff', 'Quick Fill action params prepared', {
        actionLabel: action.label,
        actionRoute: action.route || null,
        targetPage,
        affectedItems,
        normalizedItems,
        mappedQuickFillLabels,
        quickFillLabels: quickFillFocus.labels,
        suggestedCellValueCount: quickFillFocus.suggestedCellValues?.length ?? 0,
      });
    }
    return quickFillFocus
      ? {
          ...action.params,
          quickFillFocus,
        }
      : undefined;
  }

  if (action.route === 'connectorBinding') {
    return {
      connectorSections: toEnabledSectionList(
        resolveConnectorDestinationFocus(normalizedItems)
      ) as ValidationConnectorSection[],
      connectorFields: normalizedItems,
    };
  }

  if (action.route === 'processProperties') {
    return {
      processSections: toEnabledSectionList(
        resolveProcessDestinationFocus(normalizedItems)
      ) as ValidationProcessSection[],
      processFields: normalizedItems,
    };
  }

  if (action.route === 'additionalCategories') {
    return {
      additionalCategorySections: toEnabledSectionList(
        resolveAdditionalCategoriesDestinationFocus(normalizedItems)
      ) as ValidationAdditionalCategorySection[],
      additionalCategoryFields: normalizedItems,
    };
  }

  if (action.route === 'boltConnection') {
    return {
      boltFields: normalizedItems,
    };
  }

  return undefined;
}

export {
  mapQuickFillLabels,
  buildMappingReferences,
  buildMappingFocus,
  buildQuickFillFocus,
  mergeQuickFillFocus,
  mergeActionQuickFillFocus,
  toEnabledSectionList,
  resolveMappingReferenceForToken,
  normalizeFocusToken,
};
